import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControlLabel,
  Switch,
  TextField,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  Grid,
  Card,
  CardContent,
  CardHeader
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { updateAIAutomationSettings } from '../../services/providerService';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SaveIcon from '@mui/icons-material/Save';

export default function AISettingsPanel({ settings, setSettings }) {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const [aiSettings, setAISettings] = useState({
    take_notes: settings?.ai_automation?.take_notes ?? true,
    ask_questions: settings?.ai_automation?.ask_questions ?? true,
    generate_quotes: settings?.ai_automation?.generate_quotes ?? false,
    book_appointments: settings?.ai_automation?.book_appointments ?? false,
    send_reminders: settings?.ai_automation?.send_reminders ?? true,
    custom_prompt: settings?.ai_automation?.custom_prompt ?? ''
  });

  const handleSwitchChange = (event) => {
    const { name, checked } = event.target;
    setAISettings({
      ...aiSettings,
      [name]: checked
    });
  };

  const handlePromptChange = (event) => {
    setAISettings({
      ...aiSettings,
      custom_prompt: event.target.value
    });
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      await updateAIAutomationSettings(currentUser.uid, aiSettings);
      
      // Update local state
      setSettings({
        ...settings,
        ai_automation: aiSettings
      });
      
      setSnackbar({
        open: true,
        message: 'AI settings saved successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving AI settings:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save AI settings. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom>
        AI Automation Settings
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardHeader 
              title="AI Capabilities" 
              avatar={<SmartToyIcon color="primary" />}
              subheader="Configure what the AI can do for you"
            />
            <CardContent>
              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={aiSettings.take_notes}
                      onChange={handleSwitchChange}
                      name="take_notes"
                      color="primary"
                    />
                  }
                  label="AI takes notes during customer interactions"
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                  The AI will automatically document important details from customer conversations
                </Typography>
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={aiSettings.ask_questions}
                      onChange={handleSwitchChange}
                      name="ask_questions"
                      color="primary"
                    />
                  }
                  label="AI asks follow-up questions"
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                  The AI will ask customers for additional information to better understand their needs
                </Typography>
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={aiSettings.generate_quotes}
                      onChange={handleSwitchChange}
                      name="generate_quotes"
                      color="primary"
                    />
                  }
                  label="AI generates quotes automatically"
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                  The AI will create quotes based on customer requirements and your pricing
                </Typography>
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={aiSettings.book_appointments}
                      onChange={handleSwitchChange}
                      name="book_appointments"
                      color="primary"
                    />
                  }
                  label="AI books appointments automatically"
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                  The AI will schedule appointments based on your availability
                </Typography>
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={aiSettings.send_reminders}
                      onChange={handleSwitchChange}
                      name="send_reminders"
                      color="primary"
                    />
                  }
                  label="AI sends appointment reminders"
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                  The AI will send reminders to customers about upcoming appointments
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardHeader 
              title="Custom Instructions" 
              subheader="Personalize how the AI represents your business"
            />
            <CardContent>
              <TextField
                label="Custom AI Instructions"
                multiline
                rows={10}
                fullWidth
                value={aiSettings.custom_prompt}
                onChange={handlePromptChange}
                placeholder="Add specific instructions for how the AI should interact with your customers. For example: 'Always mention our 100% satisfaction guarantee' or 'Ask about the age of the home for plumbing quotes'"
                helperText="These instructions will guide how the AI communicates with your customers"
              />
              
              <Box sx={{ mt: 3 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Your custom instructions will be combined with our AI's professional knowledge of your service type.
                </Alert>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save AI Settings'}
        </Button>
      </Box>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
